import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import api from "../../../../../services/api";
import * as notifications from "../../../../../notifications/notifications";
import { NotificationsLevel } from "../../../../../lib/enums";

const DEFAULT_REFETCH_INTERVAL = 3000;

export const useRlSandboxPdfSummaryExport = hash => {
  const [pdfExportStatus, setPdfExportStatus] = useState();
  const { DOWNLOAD, WAIT, ERROR } = window.tcbase.consts.pdf_action;

  const { refetch: refetchPdfExportStatus } = useQuery({
    queryKey: ["rlSandboxPdfSummary", hash],
    queryFn: () =>
      api
        .checkRlSandboxPdfSummaryCreated(hash)
        .then(({ data: { status: newStatus } }) => {
          const isCreationRequested =
            requestPdfSummaryCreation.isSuccess ||
            requestPdfSummaryCreation.isLoading;
          const isReadyForDownload = newStatus === DOWNLOAD;
          const didFail = newStatus === ERROR;

          setPdfExportStatus(newStatus);

          if (isCreationRequested && didFail) {
            notifications.show({
              level: NotificationsLevel.ERROR,
              message: "Error while creating report..."
            });
          }

          if (isCreationRequested && isReadyForDownload) {
            downloadPdfSummary.mutate();
          }
          return newStatus;
        }),
    enabled: pdfExportStatus == null || pdfExportStatus === WAIT,
    refetchInterval: pdfExportStatus === WAIT ? DEFAULT_REFETCH_INTERVAL : null
  });

  const requestPdfSummaryCreation = useMutation({
    mutationFn: () =>
      api
        .requestRlSandboxPdfSummaryCreation(hash)
        .then(() => {
          notifications.show({
            level: NotificationsLevel.INFO,
            message: "Creation started..."
          });
        })
        .catch(() => {
          notifications.show({
            level: NotificationsLevel.ERROR,
            message: "Error while creating report..."
          });
        })
        .finally(refetchPdfExportStatus)
  });

  const downloadPdfSummary = useMutation({
    mutationFn: () => {
      notifications.show({
        level: NotificationsLevel.INFO,
        message: "Download starting..."
      });

      return api.downloadRlSandboxPdfSummary(hash);
    }
  });

  const downloadOrRequestPdfCreation = () => {
    if (pdfExportStatus === DOWNLOAD) {
      downloadPdfSummary.mutate();
    } else {
      requestPdfSummaryCreation.mutate();
    }
  };

  return {
    pdfExportStatus,
    downloadOrRequestPdfCreation
  };
};

export const useRlSandboxHtmlSummaryExport = hash => {
  const [htmlExportStatus, setHtmlExportStatus] = useState();
  const { DOWNLOAD, WAIT, ERROR } = window.tcbase.consts.pdf_action;

  const { refetch: refetchHtmlExportStatus } = useQuery({
    queryKey: ["rlSandboxHtmlSummary", hash],
    queryFn: () =>
      api
        .checkRlSandboxHtmlSummaryCreated(hash)
        .then(({ data: { status: newStatus } }) => {
          const isCreationRequested =
            requestHtmlSummaryCreation.isSuccess ||
            requestHtmlSummaryCreation.isLoading;
          const isReadyForDownload = newStatus === DOWNLOAD;
          const didFail = newStatus === ERROR;

          setHtmlExportStatus(newStatus);

          if (isCreationRequested && didFail) {
            notifications.show({
              level: NotificationsLevel.ERROR,
              message: "Error while creating report..."
            });
          }

          if (isCreationRequested && isReadyForDownload) {
            downloadHtmlSummary.mutate();
          }
          return newStatus;
        }),
    enabled: htmlExportStatus == null || htmlExportStatus === WAIT,
    refetchInterval: htmlExportStatus === WAIT ? DEFAULT_REFETCH_INTERVAL : null
  });

  const requestHtmlSummaryCreation = useMutation({
    mutationFn: () =>
      api
        .requestRlSandboxHtmlSummaryCreation(hash)
        .then(() => {
          notifications.show({
            level: NotificationsLevel.INFO,
            message: "Creation started..."
          });
        })
        .catch(() => {
          notifications.show({
            level: NotificationsLevel.ERROR,
            message: "Error while creating report..."
          });
        })
        .finally(refetchHtmlExportStatus)
  });

  const downloadHtmlSummary = useMutation({
    mutationFn: () => {
      notifications.show({
        level: NotificationsLevel.INFO,
        message: "Download starting..."
      });

      return api.downloadRlSandboxHtmlSummary(hash);
    }
  });

  const downloadOrRequestHtmlCreation = () => {
    if (htmlExportStatus === DOWNLOAD) {
      downloadHtmlSummary.mutate();
    } else {
      requestHtmlSummaryCreation.mutate();
    }
  };

  return {
    htmlExportStatus,
    downloadOrRequestHtmlCreation
  };
};
